import MenuBookIcon from "@mui/icons-material/MenuBook";
import {
  Box,
  Button,
  CardActions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { ChangeEvent, ReactNode, useState } from "react";
import { useFragment } from "react-relay";
import { CustomerAccountsCard_brand$key } from "./__generated__/CustomerAccountsCard_brand.graphql";
import CardSection from "../../../../components/CardSection";
import CopyButton from "../../../../components/CopyButton";
import LeftRight from "../../../../components/LeftRight";
import SaveButton, { SavedState } from "../../../../components/SaveButton";
import useBrandUpdateMutation from "../../../../mutations/useBrandUpdateMutation";

const brandFragment = graphql`
  fragment CustomerAccountsCard_brand on BrandType {
    ...useBrandUpdateMutation_brand
    shopifyConfig {
      shopifyUrl
      isNewCustomerAccountsEnabled
      customerAuthenticationClientId
    }
  }
`;

const CopyableValue = ({ value }: { value: string }) => (
  <TextField
    fullWidth
    size="small"
    margin="dense"
    value={value}
    InputProps={{
      readOnly: true,
      endAdornment: (
        <InputAdornment position="end">
          <CopyButton textToCopy={value} isIconButton />
        </InputAdornment>
      ),
    }}
  />
);

const SetupStep = ({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) => (
  <Stack spacing={1}>
    <Typography variant="subtitle1" fontWeight={700}>
      {number}. {title}
    </Typography>
    <Typography
      component="ul"
      variant="body2"
      sx={{ m: 0, pl: 3, "& li": { mb: 1 } }}
    >
      {children}
    </Typography>
  </Stack>
);

const CustomerAccountsSetupDialog = ({
  isOpen,
  shopifyUrl,
  onClose,
}: {
  isOpen: boolean;
  shopifyUrl: string;
  onClose: () => void;
}) => (
  <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Set up Shopify customer accounts</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={3}>
        <DialogContentText>
          You'll need a Shopify staff account with the <b>Apps and channels</b>{" "}
          permission. Each step happens in your Shopify admin unless noted.
        </DialogContentText>
        <SetupStep number={1} title="Check that new customer accounts is on">
          <li>
            Shopify has automatically switched some stores to new customer
            accounts, so yours may already be set up.
          </li>
          <li>
            Go to{" "}
            {shopifyUrl ? (
              <Link
                href={`https://${shopifyUrl}/admin/settings/customer_accounts`}
                target="_blank"
                rel="noopener"
              >
                Settings → Customer accounts
              </Link>
            ) : (
              <b>Settings → Customer accounts</b>
            )}
            .
          </li>
          <li>
            If <b>Accounts in online store and checkout</b> already shows{" "}
            <b>Customer accounts</b> (not <b>Legacy customer accounts</b>),
            you're all set. Skip to step 2.
          </li>
          <li>
            Otherwise, click <b>Edit</b>, choose <b>Customer accounts</b>, then
            click <b>Save</b>. This also switches your online store and checkout
            to new customer accounts.
          </li>
        </SetupStep>
        <SetupStep number={2} title="Create a Headless storefront">
          <li>
            Install the{" "}
            <Link
              href="https://apps.shopify.com/headless"
              target="_blank"
              rel="noopener"
            >
              Headless sales channel
            </Link>{" "}
            from the Shopify App Store.
          </li>
          <li>
            Under <b>Sales channels</b>, click <b>Headless</b>, then click{" "}
            <b>Create storefront</b>.
          </li>
        </SetupStep>
        <SetupStep number={3} title="Set up Customer Account API access">
          <li>
            In your new storefront, click <b>Manage</b> next to{" "}
            <b>Customer Account API</b>.
          </li>
          <li>
            Set the client type to <b>Public</b>. If Shopify asks what kind of
            application it is, choose <b>Web application</b>.
          </li>
          <li>
            Under <b>Application setup</b>, click <b>Edit</b> next to{" "}
            <b>Callback URI(s)</b>, add this URL, then click <b>Save</b>:
            <CopyableValue value="https://app.getkinn.com/api/v1/shopify/customer-auth/callback/" />
          </li>
          <li>
            Click <b>Edit</b> next to <b>JavaScript origin(s)</b>, add this
            origin, then click <b>Save</b>:
            <CopyableValue value="https://app.getkinn.com" />
          </li>
        </SetupStep>
        <SetupStep number={4} title="Add the client ID to Kinn">
          <li>
            Under <b>Credentials</b>, copy the <b>Client ID</b>.
          </li>
          <li>
            Close these instructions, paste it into{" "}
            <b>Customer Account API Client ID</b>, and click <b>Save</b>.
          </li>
          <li>
            Turn on <b>Enable customer accounts</b>.
          </li>
        </SetupStep>
        <DialogContentText variant="body2">
          Want more detail? See{" "}
          <Link
            href="https://shopify.dev/docs/storefronts/headless/building-with-the-customer-account-api/getting-started"
            target="_blank"
            rel="noopener"
          >
            Shopify's Customer Account API guide
          </Link>
          .
        </DialogContentText>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained">
        Done
      </Button>
    </DialogActions>
  </Dialog>
);

const CustomerAccountsToggleDialog = ({
  enable,
  isSaving,
  onCancel,
  onConfirm,
}: {
  enable: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <Dialog open onClose={onCancel} fullWidth>
    <DialogTitle>
      {enable ? "Turn on customer accounts?" : "Turn off customer accounts?"}
    </DialogTitle>
    <DialogContent>
      <DialogContentText>
        {enable
          ? "Customers on a supported app version will sign in with Shopify customer accounts right away. Make sure your store has switched to new customer accounts in Shopify first, or customers won't be able to sign in."
          : "The app will stop using Shopify customer accounts for sign-in right away. If your store uses new customer accounts, customers won't be able to sign in to the app until this is turned back on. Your client ID will stay saved."}
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        color={enable ? "primary" : "error"}
        disabled={isSaving}
      >
        {enable ? "Turn on" : "Turn off"}
      </Button>
    </DialogActions>
  </Dialog>
);

const CustomerAccountsCard = ({
  brand: brandKey,
}: {
  brand: CustomerAccountsCard_brand$key;
}) => {
  const brand = useFragment<CustomerAccountsCard_brand$key>(
    brandFragment,
    brandKey
  );
  const [updateBrand, isBrandMutationInFlight] = useBrandUpdateMutation(brand);

  const isEnabled = brand.shopifyConfig?.isNewCustomerAccountsEnabled ?? false;
  const savedClientId =
    brand.shopifyConfig?.customerAuthenticationClientId ?? "";

  const [clientId, setClientId] = useState(savedClientId);
  const [saveButtonState, setSaveButtonState] = useState<SavedState>(
    savedClientId === "" ? SavedState.DISABLED : SavedState.SAVED
  );
  const [pendingEnable, setPendingEnable] = useState<boolean | null>(null);
  const [isSetupDialogOpen, setIsSetupDialogOpen] = useState(false);

  const trimmedClientId = clientId.trim();
  const isClientIdRequired = isEnabled && trimmedClientId === "";

  let status = "Off. Turn this on once your store uses new customer accounts.";
  if (isEnabled) {
    status =
      "On. Customers on a supported app version sign in with Shopify customer accounts.";
  } else if (savedClientId === "") {
    status = "Add your client ID below to turn this on.";
  }

  return (
    <>
      <CardSection
        title="Customer Accounts"
        subtitle="Sign customers in to the app with Shopify's new customer accounts."
        content={
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Why is this needed?</Typography>
              <Typography
                component="ul"
                variant="body2"
                color="text.secondary"
                sx={{ m: 0, pl: 3, "& li": { mb: 0.5 } }}
              >
                <li>
                  Shopify's new customer accounts sign customers in with a
                  one-time code sent to their email instead of a password.
                </li>
                <li>
                  Multipass only works with Shopify's legacy customer accounts,
                  so once your store switches, the app has to sign customers in
                  through Shopify's Customer Account API instead.
                </li>
                <li>
                  Shopify only issues Customer Account API credentials through a
                  Headless sales channel. Creating one gives you a client ID
                  that lets customers sign in to the app with the same account
                  they use on your online store.
                </li>
              </Typography>
            </Stack>
            <Box>
              <Button
                variant="outlined"
                startIcon={<MenuBookIcon />}
                onClick={() => setIsSetupDialogOpen(true)}
              >
                View setup instructions
              </Button>
            </Box>
            <Divider />
            <LeftRight
              expandLeft
              left={
                <Stack>
                  <Typography
                    variant="body2"
                    sx={{ color: "black", fontWeight: "bold" }}
                  >
                    Enable customer accounts
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {status}
                  </Typography>
                </Stack>
              }
              right={
                <Switch
                  checked={isEnabled}
                  disabled={
                    isBrandMutationInFlight ||
                    (!isEnabled && savedClientId === "")
                  }
                  onChange={(_event, checked) => setPendingEnable(checked)}
                />
              }
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <TextField
                fullWidth
                sx={{ flexGrow: 1, minWidth: 0 }}
                margin="normal"
                label="Customer Account API Client ID"
                placeholder="shp_00000000-0000-0000-0000-000000000000"
                variant="outlined"
                value={clientId}
                error={isClientIdRequired}
                helperText={
                  isClientIdRequired
                    ? "A client ID is required while customer accounts is on."
                    : undefined
                }
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setClientId(event.target.value);
                  setSaveButtonState(SavedState.ENABLED);
                }}
              />
              <CardActions
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  p: { xs: 0, sm: 1 },
                }}
              >
                <SaveButton
                  savedState={
                    isBrandMutationInFlight || isClientIdRequired
                      ? SavedState.DISABLED
                      : saveButtonState
                  }
                  onClick={() => {
                    updateBrand(
                      {
                        shopifyConfig: {
                          customerAuthenticationClientId:
                            trimmedClientId || null,
                        },
                      },
                      (_) => {
                        setClientId(trimmedClientId);
                        setSaveButtonState(SavedState.SAVED);
                      }
                    );
                  }}
                />
              </CardActions>
            </Stack>
          </Stack>
        }
      />
      <CustomerAccountsSetupDialog
        isOpen={isSetupDialogOpen}
        shopifyUrl={brand.shopifyConfig?.shopifyUrl ?? ""}
        onClose={() => setIsSetupDialogOpen(false)}
      />
      {pendingEnable !== null ? (
        <CustomerAccountsToggleDialog
          enable={pendingEnable}
          isSaving={isBrandMutationInFlight}
          onCancel={() => setPendingEnable(null)}
          onConfirm={() =>
            updateBrand(
              {
                shopifyConfig: { isNewCustomerAccountsEnabled: pendingEnable },
              },
              () => setPendingEnable(null)
            )
          }
        />
      ) : null}
    </>
  );
};

export default CustomerAccountsCard;
